<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import EmailMarketingTemplatesAPI from 'dashboard/api/emailMarketingTemplates';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EmailMarketingTemplateForm from 'dashboard/components-next/Campaigns/Pages/EmailMarketingTemplates/EmailMarketingTemplateForm.vue';
import EmailMarketingTemplateList from 'dashboard/components-next/Campaigns/Pages/EmailMarketingTemplates/EmailMarketingTemplateList.vue';

const route = useRoute();
const { t } = useI18n();

const templates = ref([]);
const selectedTemplate = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const isEditorOpen = ref(false);
const loadError = ref('');
const saveError = ref('');
const successMessage = ref('');
const templateFormRef = ref(null);

const sortedTemplates = computed(() =>
  [...templates.value].sort((first, second) => {
    const statusOrder = { active: 0, archived: 1 };
    const statusDifference =
      (statusOrder[first.status] ?? 3) - (statusOrder[second.status] ?? 3);
    return statusDifference || first.name.localeCompare(second.name);
  })
);

const hasTemplates = computed(() => sortedTemplates.value.length > 0);

const campaignRoute = computed(() => ({
  name: 'email_marketing_index',
  params: { accountId: route.params.accountId },
}));

const listsRoute = computed(() => ({
  name: 'email_marketing_lists',
  params: { accountId: route.params.accountId },
}));

const responseTemplates = response => {
  const payload = response.data?.payload ?? response.data ?? [];
  if (Array.isArray(payload)) return payload;
  return payload.email_marketing_templates || payload.templates || [];
};

const fetchTemplates = async () => {
  isLoading.value = true;
  loadError.value = '';
  try {
    templates.value = responseTemplates(await EmailMarketingTemplatesAPI.get());
  } catch (error) {
    templates.value = [];
    loadError.value =
      error?.response?.data?.message ||
      t('CAMPAIGN.EMAIL.TEMPLATES.ERROR_LOAD');
  } finally {
    isLoading.value = false;
  }
};

const openCreate = () => {
  selectedTemplate.value = null;
  saveError.value = '';
  successMessage.value = '';
  isEditorOpen.value = true;
};

const openEdit = template => {
  selectedTemplate.value = template;
  saveError.value = '';
  successMessage.value = '';
  isEditorOpen.value = true;
};

const closeEditor = () => {
  isEditorOpen.value = false;
  selectedTemplate.value = null;
  saveError.value = '';
};

const requestCloseEditor = () => {
  if (templateFormRef.value?.isDirty) {
    // eslint-disable-next-line no-alert
    const shouldDiscard = window.confirm(
      t('CAMPAIGN.EMAIL.TEMPLATES.CONFIRM_DISCARD')
    );
    if (!shouldDiscard) return;
  }

  closeEditor();
};

const saveTemplate = async values => {
  isSaving.value = true;
  saveError.value = '';
  const payload = { email_marketing_template: values };

  try {
    if (selectedTemplate.value?.id) {
      await EmailMarketingTemplatesAPI.update(
        selectedTemplate.value.id,
        payload
      );
      successMessage.value = t('CAMPAIGN.EMAIL.TEMPLATES.SUCCESS_UPDATE');
    } else {
      await EmailMarketingTemplatesAPI.create(payload);
      successMessage.value = t('CAMPAIGN.EMAIL.TEMPLATES.SUCCESS_CREATE');
    }
    closeEditor();
    await fetchTemplates();
  } catch (error) {
    saveError.value =
      error?.response?.data?.message ||
      t('CAMPAIGN.EMAIL.TEMPLATES.ERROR_SAVE');
  } finally {
    isSaving.value = false;
  }
};

const archiveTemplate = async template => {
  const message = t('CAMPAIGN.EMAIL.TEMPLATES.CONFIRM_ARCHIVE', {
    name: template.name,
  });
  // eslint-disable-next-line no-alert
  if (!window.confirm(message)) return;

  loadError.value = '';
  try {
    await EmailMarketingTemplatesAPI.archive(template.id);
    successMessage.value = t('CAMPAIGN.EMAIL.TEMPLATES.SUCCESS_ARCHIVE');
    await fetchTemplates();
  } catch (error) {
    loadError.value =
      error?.response?.data?.message ||
      t('CAMPAIGN.EMAIL.TEMPLATES.ERROR_ARCHIVE');
  }
};

onMounted(fetchTemplates);
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.EMAIL.TEMPLATES.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.EMAIL.TEMPLATES.NEW_TEMPLATE')"
    @click="openCreate"
  >
    <div class="flex flex-col gap-5">
      <nav
        class="flex flex-wrap items-center gap-2 border-b border-n-weak pb-4"
      >
        <router-link
          :to="campaignRoute"
          class="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        >
          <span class="i-lucide-megaphone size-4" />
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.CAMPAIGNS_LINK') }}
        </router-link>
        <router-link
          :to="listsRoute"
          class="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        >
          <span class="i-lucide-list size-4" />
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.LISTS_LINK') }}
        </router-link>
      </nav>

      <p
        v-if="successMessage"
        class="border-l-2 border-n-teal-9 py-1 pl-3 text-sm text-n-teal-11"
      >
        {{ successMessage }}
      </p>

      <div
        v-if="isLoading"
        data-testid="email-templates-loading"
        class="flex items-center justify-center py-16 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="loadError"
        data-testid="email-templates-error"
        class="flex flex-col items-center gap-3 border-y border-n-weak py-14 text-center"
      >
        <span class="i-lucide-circle-alert size-6 text-n-ruby-10" />
        <p class="text-sm text-n-slate-11">{{ loadError }}</p>
        <Button
          size="sm"
          variant="faded"
          color="slate"
          icon="i-lucide-refresh-cw"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.RETRY')"
          @click="fetchTemplates"
        />
      </div>

      <EmailMarketingTemplateList
        v-else-if="hasTemplates"
        :templates="sortedTemplates"
        @edit="openEdit"
        @archive="archiveTemplate"
      />

      <div
        v-else
        data-testid="email-templates-empty"
        class="flex flex-col items-center border-y border-dashed border-n-weak py-16 text-center"
      >
        <span class="i-lucide-layout-template mb-3 size-7 text-n-slate-10" />
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.EMPTY_TITLE') }}
        </h2>
        <p class="mt-1 max-w-md text-sm text-n-slate-10">
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.EMPTY_SUBTITLE') }}
        </p>
        <Button
          class="mt-4"
          size="sm"
          icon="i-lucide-plus"
          :label="t('CAMPAIGN.EMAIL.TEMPLATES.NEW_TEMPLATE')"
          @click="openCreate"
        />
      </div>
    </div>

    <div
      v-if="isEditorOpen"
      data-testid="email-template-editor"
      class="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <section
        class="flex h-full w-full max-w-[90rem] flex-col border-l border-n-weak bg-n-solid-2 shadow-xl"
      >
        <header
          class="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-n-weak px-5 sm:px-6"
        >
          <div class="min-w-0">
            <h2 class="truncate text-base font-medium text-n-slate-12">
              {{
                selectedTemplate
                  ? t('CAMPAIGN.EMAIL.TEMPLATES.EDITOR.EDIT')
                  : t('CAMPAIGN.EMAIL.TEMPLATES.EDITOR.CREATE')
              }}
            </h2>
            <p v-if="selectedTemplate" class="truncate text-xs text-n-slate-10">
              {{ selectedTemplate.name }}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            :title="t('CAMPAIGN.EMAIL.TEMPLATES.FORM.CANCEL')"
            @click="requestCloseEditor"
          />
        </header>
        <EmailMarketingTemplateForm
          ref="templateFormRef"
          :template="selectedTemplate"
          :is-saving="isSaving"
          :error="saveError"
          @save="saveTemplate"
          @cancel="requestCloseEditor"
        />
      </section>
    </div>
  </CampaignLayout>
</template>
